/// ///////////////////////////////////////////////////////////////////////////////
// Copyright (C) 2025 CONQUER INNOVATIONS SP. Z O.O. Global B.V. and its affiliate(s).
// All rights reserved.
//
// This software and other materials contain proprietary information
// controlled by CONQUER INNOVATIONS SP. Z O.O. and are protected by applicable copyright legislation.
// Any use and utilization of this software and other materials and
// disclosure to any third parties is conditional upon having a separate
// agreement with CONQUER INNOVATIONS SP. Z O.O. for the access, use, utilization or disclosure of this
// software. In the absence of such agreement, the use of the software is not
// allowed.
// The creator, author and originator of the code in the current file: Michał Pawłowski
//////////////////////////////////////////////////////////////////////////////////
<template>
  <b-modal
      v-model="isVisible"
      id="selling-ranges-modal"
      title="Parametry analizy sprzedaży"
      title-class="font-18 text-primary fw-bold"
      header-class="bg-light-gray"
      size="lg"
      hide-footer
      @hide="onClose"
  >
    <div class="p-2">
      <div class="d-flex justify-content-between align-items-center mb-3">
        <div>
          <span class="text-secondary fw-medium">
            Zdefiniuj okresy sprzedaży brane pod uwagę w analizie.
          </span>
        </div>
        <button
            class="btn btn-primary btn-sm shadow-sm d-flex align-items-center gap-2"
            @click="addNewRange"
            :disabled="ranges.length >= maxRanges"
        >
          <i class="fas fa-plus"></i> Dodaj okres
        </button>
      </div>

      <div v-if="ranges.length >= maxRanges" class="alert alert-warning py-1 px-2 small mb-3">
        <i class="fas fa-info-circle me-1"></i> Osiągnięto maksymalną liczbę okresów ({{ maxRanges }}).
      </div>

      <div class="ranges-container border rounded bg-white">
        <div class="row g-0 border-bottom bg-light fw-bold text-muted small py-2 px-2">
          <div class="col-1 text-center">Priorytet</div>
          <div class="col-2 ps-2">Nazwa</div>
          <div class="col-4 px-2">Data od</div>
          <div class="col-4 px-2">Data do</div>
          <div class="col-1 text-center">Akcje</div>
        </div>

        <div v-if="ranges.length === 0" class="text-center p-4 text-muted fst-italic">
          Brak zdefiniowanych okresów. Dodaj pierwszy zakres.
        </div>

        <div
            v-for="(range, index) in ranges"
            :key="range.guid || index"
            class="row g-0 border-bottom align-items-center py-2 px-2 hover-row"
        >
          <div class="col-1 d-flex flex-column align-items-center justify-content-center gap-1">
            <button
                class="btn btn-sm btn-link text-decoration-none p-0 text-secondary"
                :disabled="index === 0"
                @click="moveRange(index, -1)"
                title="Przesuń wyżej"
            >
              <i class="fas fa-chevron-up"></i>
            </button>
            <button
                class="btn btn-sm btn-link text-decoration-none p-0 text-secondary"
                :disabled="index === ranges.length - 1"
                @click="moveRange(index, 1)"
                title="Przesuń niżej"
            >
              <i class="fas fa-chevron-down"></i>
            </button>
          </div>

          <div class="col-2 ps-2 fw-bold text-dark">
            Okres {{ index + 1 }}
          </div>

          <div class="col-4 px-2">
            <input
                type="date"
                class="form-control form-control-sm"
                v-model="range.startDateInput"
                :class="{'is-invalid': !range.startDateInput || isDateInvalid(range)}"
                @change="onRangeChange(range)"
            />
          </div>

          <div class="col-4 px-2">
            <input
                type="date"
                class="form-control form-control-sm"
                v-model="range.endDateInput"
                :class="{'is-invalid': !range.endDateInput || isDateInvalid(range)}"
                @change="onRangeChange(range)"
            />
            <div class="invalid-feedback" v-if="isDateInvalid(range)">
              Data 'Od' musi być wcześniejsza niż 'Do'.
            </div>
          </div>

          <div class="col-1 text-center">
            <button
                class="btn btn-sm btn-outline-danger border-0"
                @click="confirmRemoveRange(range)"
                title="Usuń okres"
            >
              <i class="fas fa-trash-alt"></i>
            </button>
          </div>
        </div>
      </div>

      <div class="d-flex justify-content-end mt-3 pt-2 border-top">
        <button class="btn btn-secondary" @click="closeModal">Zamknij</button>
      </div>
    </div>
  </b-modal>
</template>

<script>
import { BModal } from "bootstrap-vue-next";
import Swal from "sweetalert2";
import dateFormat from "dateformat";
import { usePackagesManagerModule } from "@/state/pinia/packagesManagerModule";


export default {
  name: "ProductsSellingCustomDateRangesModal",
  components: { BModal },
  props: {
    showModal: {
      type: Boolean,
      required: true
    },
    packageOrderGroupGuid: {
      type: String,
      required: true
    }
  },
  emits: ["update:showModal", "rangesUpdated"],
  data() {
    return {
      maxRanges: 6,
      ranges: [], // Lokalna kopia danych do edycji
      isProcessing: false
    };
  },
  computed: {
    isVisible: {
      get() {
        return this.showModal;
      },
      set(val) {
        this.$emit("update:showModal", val);
      }
    },
    packagesModule() {
      return usePackagesManagerModule();
    },
  },
  watch: {
    showModal(val) {
      if (val) {
        this.fetchData();
      } else {
        this.ranges = [];
        this.packagesModule.clearSellingCustomDateRanges();
      }
    }
  },
  methods: {
    fetchData() {
      if (!this.packageOrderGroupGuid) return;

      this.packagesModule.httpGetSellingCustomDateRanges({
        packageOrderGroupGuid: this.packageOrderGroupGuid
      }).then((data) => {
        if (data) {
          // Mapowanie danych z backendu na format przyjazny inputom (YYYY-MM-DD)
          console.log("Fetched ranges data:", data);
          this.ranges = data.map(r => ({
            ...r,
            startDateInput: this.formatDateForInput(r.startDate),
            endDateInput: this.formatDateForInput(r.endDate)
          }));
        }
      });
    },

    formatDateForInput(timestamp) {
      if (!timestamp) return "";
      // timestamp może być liczbą (epoch) lub stringiem ISO
      return dateFormat(new Date(timestamp), "yyyy-mm-dd");
    },

    isDateInvalid(range) {
      if (!range.startDateInput || !range.endDateInput) return false;
      return new Date(range.startDateInput) > new Date(range.endDateInput);
    },

    addNewRange() {
      if (this.ranges.length >= this.maxRanges) return;

      let newStartTimestamp, newEndTimestamp;

      if (this.ranges.length === 0) {
        // Jeśli brak zakresów: Domyślnie od "dzisiaj - 1 miesiąc" do "dzisiaj"
        const now = new Date();
        const endD = new Date(now);
        const startD = new Date(now);
        startD.setMonth(startD.getMonth() - 1);

        newStartTimestamp = startD.getTime();
        newEndTimestamp = endD.getTime();
      } else {
        // 1. Znajdź najwcześniejszą datę startu z istniejących zakresów
        // Używamy Math.min na timestampach
        const currentMinStart = Math.min(...this.ranges.map(r => r.startDate));

        // 2. Ustal nową datę końcową (End) jako jeden dzień przed najwcześniejszym startem
        // Dzięki temu zakresy będą się stykać, ale nie nakładać (ciągłość)
        const refDate = new Date(currentMinStart);
        const endD = new Date(refDate);
        endD.setDate(endD.getDate() - 1);

        // 3. Ustal nową datę początkową (Start) jako miesiąc wstecz od nowej daty końcowej
        const startD = new Date(endD);
        startD.setMonth(startD.getMonth() - 1);

        newStartTimestamp = startD.getTime();
        newEndTimestamp = endD.getTime();
      }

      const newRangeDto = {
        packageOrderGroupGuid: this.packageOrderGroupGuid,
        listIndex: this.ranges.length,
        // Backend oczekuje Timestamp (long)
        startDate: newStartTimestamp,
        endDate: newEndTimestamp
      };

      this.packagesModule.httpCreateSellingCustomDateRange({
        packageOrderGroupGuid: this.packageOrderGroupGuid,
        dto: newRangeDto
      }).then((response) => {
        if (response && response.status === 200) {
          this.fetchData();
          this.$emit("rangesUpdated");
        }
      });
    },

    onRangeChange(range) {
      // Walidacja przed wysyłką
      if (!range.startDateInput || !range.endDateInput) return; // Czekamy na obie daty
      if (this.isDateInvalid(range)) {
        // Opcjonalnie: Swal alert, że data jest zła
        return;
      }

      const dto = {
        guid: range.guid,
        packageOrderGroupGuid: this.packageOrderGroupGuid,
        rangeKey: range.rangeKey,
        listIndex: range.listIndex,
        startDate: new Date(range.startDateInput).getTime(),
        endDate: new Date(range.endDateInput).getTime()
      };

      console.log("Updating range with DTO:", range);

      this.packagesModule.httpUpdateSellingCustomDateRange({
        rangeGuid: range.guid,
        dto: dto
      }).then(() => {
        this.$emit("rangesUpdated");
      });
    },

    confirmRemoveRange(range) {
      Swal.fire({
        title: "Czy na pewno usunąć ten okres?",
        text: "Operacja jest nieodwracalna.",
        icon: "warning",
        showCancelButton: true,
        confirmButtonColor: "#dc3545",
        cancelButtonColor: "#6c757d",
        confirmButtonText: "Tak, usuń",
        cancelButtonText: "Anuluj"
      }).then((result) => {
        if (result.isConfirmed) {
          this.removeRange(range);
        }
      });
    },

    removeRange(range) {
      this.packagesModule.httpDeleteSellingCustomDateRange({
        rangeGuid: range.guid
      }).then((response) => {
        if (response && response.status === 200) {
          this.fetchData();
          this.$emit("rangesUpdated");
        }
      });
    },

    moveRange(index, direction) {
      const newIndex = index + direction;
      if (newIndex < 0 || newIndex >= this.ranges.length) return;

      // Swap logic
      const itemA = this.ranges[index];
      const itemB = this.ranges[newIndex];

      // Zamiana listIndex w obiektach
      const tempIndex = itemA.listIndex;
      itemA.listIndex = itemB.listIndex;
      itemB.listIndex = tempIndex;

      // Optimistyczna aktualizacja UI (swap w tablicy)
      this.ranges[index] = itemB;
      this.ranges[newIndex] = itemA;

      // Wyślij update do API dla obu elementów
      // Ponieważ nie ma bulk update, robimy dwa requesty.
      // Warto obsłużyć Promise.all, żeby mieć pewność spójności.

      const payloadA = this.createDtoFromRange(itemA);
      const payloadB = this.createDtoFromRange(itemB);

      Promise.all([
        this.packagesModule.httpUpdateSellingCustomDateRange({
          rangeGuid: itemA.guid,
          dto: payloadA
        }),
        this.packagesModule.httpUpdateSellingCustomDateRange({
          rangeGuid: itemB.guid,
          dto: payloadB
        })
      ]).then(() => {
        // Po sukcesie odświeżamy z bazy, aby mieć pewność co do kolejności
        this.fetchData();
        this.$emit("rangesUpdated");
      });
    },

    createDtoFromRange(range) {
      return {
        guid: range.guid,
        packageOrderGroupGuid: this.packageOrderGroupGuid,
        rangeKey: range.rangeKey,
        listIndex: range.listIndex,
        startDate: new Date(range.startDateInput).getTime(),
        endDate: new Date(range.endDateInput).getTime()
      };
    },

    closeModal() {
      this.isVisible = false;
    },
    onClose() {
      this.packagesModule.clearSellingCustomDateRanges();
    }
  }
};
</script>

<style scoped>
.bg-light-gray {
  background-color: #f3f2f1;
}

.ranges-container {
  max-height: 400px;
  overflow-y: auto;
}

.hover-row:hover {
  background-color: #f8f9fa;
}

/* Dostosowanie inputów daty */
input[type="date"] {
  font-family: "Segoe UI", sans-serif;
  font-size: 0.9rem;
}

/* Stylizacja przycisków strzałek */
.btn-link {
  color: #6c757d;
}
.btn-link:hover {
  color: #0078D4;
}
.btn-link:disabled {
  color: #dee2e6;
  cursor: default;
}
</style>

